import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Tabs, Tab } from 'react-bootstrap';
import axios from 'axios';

const AddOndutyRequest = ({ show, onHide, employees, onOndutyAdded }) => {
    const [activeTab, setActiveTab] = useState('date-based');
    const [formData, setFormData] = useState({
        emp_id: '',
        request_type: 'Full Day',
        timing_mode: 'Date Based',
        // Date-based fields
        from_date: new Date().toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0],
        first_day_half: false,
        first_day_period: 'Morning',
        last_day_half: false,
        last_day_period: 'Morning',
        // Time-based fields
        from_datetime: '',
        to_datetime: '',
        time_slot_type: 'Morning',
        custom_start_time: '',
        custom_end_time: '',
        // Common fields
        purpose: '',
        destination: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [totalDays, setTotalDays] = useState(1);
    const [totalHours, setTotalHours] = useState(0);

    // Autocomplete states
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const searchRef = useRef(null);
    const suggestionsRef = useRef(null);

    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

    // Filter employees based on search query
    useEffect(() => {
        if (searchQuery.trim() === '') {
            setFilteredEmployees([]);
            return;
        }

        const query = searchQuery.toLowerCase();
        const filtered = employees
            .filter(emp => emp.status === 'Active')
            .filter(emp =>
                emp.first_name?.toLowerCase().includes(query) ||
                emp.last_name?.toLowerCase().includes(query) ||
                emp.emp_code?.toLowerCase().includes(query) ||
                emp.email?.toLowerCase().includes(query) ||
                `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(query)
            )
            .slice(0, 10);

        setFilteredEmployees(filtered);
        setShowSuggestions(filtered.length > 0);
    }, [searchQuery, employees]);

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                searchRef.current &&
                !searchRef.current.contains(event.target) &&
                suggestionsRef.current &&
                !suggestionsRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Calculate total days for date-based requests
    useEffect(() => {
        if (activeTab === 'date-based' && formData.from_date && formData.to_date) {
            const start = new Date(formData.from_date);
            const end = new Date(formData.to_date);
            
            if (end >= start) {
                const diffTime = Math.abs(end - start);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // Inclusive
                
                let total = diffDays;
                if (formData.first_day_half) total -= 0.5;
                if (formData.last_day_half) total -= 0.5;
                
                setTotalDays(total);
                
                // Set request type based on duration
                if (diffDays === 1) {
                    if (formData.first_day_half || formData.last_day_half) {
                        setFormData(prev => ({ ...prev, request_type: 'Half Day' }));
                    } else {
                        setFormData(prev => ({ ...prev, request_type: 'Full Day' }));
                    }
                } else {
                    setFormData(prev => ({ ...prev, request_type: 'Multiple Days' }));
                }
            } else {
                setTotalDays(0);
                setError('To date must be after or equal to from date');
            }
        }
    }, [formData.from_date, formData.to_date, formData.first_day_half, formData.last_day_half, activeTab]);

    // Calculate total hours for time-based requests
    useEffect(() => {
        if (activeTab === 'time-based') {
            if (formData.time_slot_type === 'Custom' && formData.custom_start_time && formData.custom_end_time) {
                const start = new Date(`1970-01-01T${formData.custom_start_time}`);
                const end = new Date(`1970-01-01T${formData.custom_end_time}`);
                const diffMs = end - start;
                const hours = (diffMs / (1000 * 60 * 60)).toFixed(2);
                setTotalHours(parseFloat(hours) || 0);
                setFormData(prev => ({ ...prev, request_type: 'Specific Hours' }));
            } else if (formData.from_datetime && formData.to_datetime) {
                const start = new Date(formData.from_datetime);
                const end = new Date(formData.to_datetime);
                const diffMs = end - start;
                const hours = (diffMs / (1000 * 60 * 60)).toFixed(2);
                setTotalHours(parseFloat(hours) || 0);
                setFormData(prev => ({ ...prev, request_type: 'Specific Hours' }));
            }
        }
    }, [
        formData.time_slot_type, 
        formData.custom_start_time, 
        formData.custom_end_time,
        formData.from_datetime,
        formData.to_datetime,
        activeTab
    ]);

    const handleEmployeeSelect = (employee) => {
        setFormData(prev => ({
            ...prev,
            emp_id: employee.emp_id
        }));
        setSelectedEmployee(employee);
        setSearchQuery(`${employee.first_name} ${employee.last_name} (${employee.emp_code})`);
        setShowSuggestions(false);
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);

        if (!value.trim()) {
            setFormData(prev => ({ ...prev, emp_id: '' }));
            setSelectedEmployee(null);
            setShowSuggestions(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setError('');
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setFormData(prev => ({
            ...prev,
            timing_mode: tab === 'date-based' ? 'Date Based' : 'Time Based',
            request_type: tab === 'date-based' ? 'Full Day' : 'Specific Hours'
        }));
        setError('');
    };

    const validateForm = () => {
        if (!formData.emp_id) {
            setError('Please select an employee');
            return false;
        }

        if (!formData.purpose.trim()) {
            setError('Please provide a purpose');
            return false;
        }

        if (activeTab === 'date-based') {
            if (!formData.from_date || !formData.to_date) {
                setError('Please select both from and to dates');
                return false;
            }

            if (new Date(formData.to_date) < new Date(formData.from_date)) {
                setError('To date must be after or equal to from date');
                return false;
            }

            if (formData.first_day_half && !formData.first_day_period) {
                setError('Please select period for first day half');
                return false;
            }

            if (formData.last_day_half && !formData.last_day_period) {
                setError('Please select period for last day half');
                return false;
            }
        } else { // time-based
            if (formData.time_slot_type === 'Custom') {
                if (!formData.custom_start_time || !formData.custom_end_time) {
                    setError('Please select both start and end times');
                    return false;
                }

                const start = new Date(`1970-01-01T${formData.custom_start_time}`);
                const end = new Date(`1970-01-01T${formData.custom_end_time}`);
                if (end <= start) {
                    setError('End time must be after start time');
                    return false;
                }
            } else {
                if (!formData.from_datetime || !formData.to_datetime) {
                    setError('Please select both from and to date-time');
                    return false;
                }

                if (new Date(formData.to_datetime) <= new Date(formData.from_datetime)) {
                    setError('To date-time must be after from date-time');
                    return false;
                }
            }
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        try {
            setLoading(true);
            setError('');

            const token = localStorage.getItem('authToken');
            
            // Prepare data based on active tab
            const submitData = {
                emp_id: formData.emp_id,
                request_type: formData.request_type,
                timing_mode: formData.timing_mode,
                purpose: formData.purpose,
                destination: formData.destination || null
            };

            if (activeTab === 'date-based') {
                submitData.from_date = formData.from_date;
                submitData.to_date = formData.to_date;
                submitData.total_days = totalDays;
                submitData.first_day_half = formData.first_day_half ? 1 : 0;
                submitData.first_day_period = formData.first_day_half ? formData.first_day_period : null;
                submitData.last_day_half = formData.last_day_half ? 1 : 0;
                submitData.last_day_period = formData.last_day_half ? formData.last_day_period : null;
            } else {
                if (formData.time_slot_type === 'Custom') {
                    // For custom time slots within a single day
                    const today = new Date().toISOString().split('T')[0];
                    submitData.from_datetime = `${today}T${formData.custom_start_time}`;
                    submitData.to_datetime = `${today}T${formData.custom_end_time}`;
                    submitData.time_slot_type = 'Custom';
                    submitData.custom_start_time = formData.custom_start_time;
                    submitData.custom_end_time = formData.custom_end_time;
                } else {
                    submitData.from_datetime = formData.from_datetime;
                    submitData.to_datetime = formData.to_datetime;
                    submitData.time_slot_type = formData.time_slot_type;
                }
                submitData.total_hours = totalHours;
            }

            const response = await axios.post(`${API_BASE}onduty/add_onduty_request.php`,
                submitData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                alert('On-duty request submitted successfully');

                // Reset form
                setFormData({
                    emp_id: '',
                    request_type: 'Full Day',
                    timing_mode: 'Date Based',
                    from_date: new Date().toISOString().split('T')[0],
                    to_date: new Date().toISOString().split('T')[0],
                    first_day_half: false,
                    first_day_period: 'Morning',
                    last_day_half: false,
                    last_day_period: 'Morning',
                    from_datetime: '',
                    to_datetime: '',
                    time_slot_type: 'Morning',
                    custom_start_time: '',
                    custom_end_time: '',
                    purpose: '',
                    destination: ''
                });
                setSearchQuery('');
                setSelectedEmployee(null);
                setTotalDays(1);
                setTotalHours(0);
                setActiveTab('date-based');

                onOndutyAdded();
                onHide();
            } else {
                setError(response.data.error || 'Failed to submit onduty request');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to submit onduty request');
            console.error('Error submitting onduty request:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFocus = () => {
        if (searchQuery && filteredEmployees.length > 0) {
            setShowSuggestions(true);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton className="bg-primary text-white">
                <Modal.Title>
                    <i className="bi bi-plus-circle me-2"></i>
                    New On-Duty Request
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && (
                        <Alert variant="danger" className="d-flex align-items-center">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            <div>{error}</div>
                        </Alert>
                    )}

                    <Row className="g-3">
                        {/* Employee Search with Autocomplete */}
                        <Col md={12}>
                            <Form.Group controlId="employeeSearch" ref={searchRef}>
                                <Form.Label>Search Employee <span className="text-danger">*</span></Form.Label>
                                <div className="position-relative">
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0">
                                            <i className="bi bi-search text-muted"></i>
                                        </span>
                                        <Form.Control
                                            type="text"
                                            placeholder="Type employee name, code, or email..."
                                            value={searchQuery}
                                            onChange={handleSearchChange}
                                            onFocus={handleFocus}
                                            required
                                            autoComplete="off"
                                        />
                                        {searchQuery && (
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary border-start-0"
                                                onClick={() => {
                                                    setSearchQuery('');
                                                    setFormData(prev => ({ ...prev, emp_id: '' }));
                                                    setSelectedEmployee(null);
                                                    setShowSuggestions(false);
                                                }}
                                            >
                                                <i className="bi bi-x"></i>
                                            </button>
                                        )}
                                    </div>

                                    {/* Autocomplete Suggestions */}
                                    {showSuggestions && filteredEmployees.length > 0 && (
                                        <div
                                            ref={suggestionsRef}
                                            className="position-absolute w-100 bg-white border rounded shadow-lg mt-1"
                                            style={{
                                                zIndex: 1050,
                                                maxHeight: '300px',
                                                overflowY: 'auto'
                                            }}
                                        >
                                            <div className="list-group list-group-flush">
                                                {filteredEmployees.map((employee) => (
                                                    <button
                                                        key={employee.emp_id}
                                                        type="button"
                                                        className="list-group-item list-group-item-action border-0 py-2 px-3"
                                                        onClick={() => handleEmployeeSelect(employee)}
                                                    >
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <div className="fw-semibold">
                                                                    {employee.first_name} {employee.last_name}
                                                                </div>
                                                                <small className="text-muted">
                                                                    Code: {employee.emp_code} | ID: {employee.emp_id}
                                                                </small>
                                                            </div>
                                                            <div className="text-end">
                                                                <small className="text-success">
                                                                    {employee.department || 'No Department'}
                                                                </small>
                                                                <br />
                                                                <small className="text-muted">
                                                                    {employee.email || 'No email'}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Selected Employee Info */}
                                {selectedEmployee && (
                                    <div className="mt-3 p-3 bg-light rounded">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <h6 className="mb-1 fw-bold">
                                                    <i className="bi bi-person-check me-2 text-success"></i>
                                                    Selected Employee
                                                </h6>
                                                <p className="mb-0">
                                                    {selectedEmployee.first_name} {selectedEmployee.last_name}
                                                    <span className="text-muted ms-2">
                                                        ({selectedEmployee.emp_code}) | ID: {selectedEmployee.emp_id}
                                                    </span>
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger btn-sm"
                                                onClick={() => {
                                                    setSelectedEmployee(null);
                                                    setSearchQuery('');
                                                    setFormData(prev => ({ ...prev, emp_id: '' }));
                                                }}
                                            >
                                                <i className="bi bi-x"></i> Clear
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </Form.Group>
                        </Col>

                        {/* Hidden input for emp_id */}
                        <Form.Control
                            type="hidden"
                            name="emp_id"
                            value={formData.emp_id}
                            required
                        />

                        <Col md={12}>
                            <Tabs
                                activeKey={activeTab}
                                onSelect={(k) => handleTabChange(k)}
                                className="mb-3"
                            >
                                <Tab eventKey="date-based" title="Date Based">
                                    <Row className="g-3 mt-2">
                                        <Col md={6}>
                                            <Form.Group controlId="from_date">
                                                <Form.Label>From Date <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="from_date"
                                                    value={formData.from_date}
                                                    onChange={handleChange}
                                                    min={new Date().toISOString().split('T')[0]}
                                                    required={activeTab === 'date-based'}
                                                    disabled={activeTab !== 'date-based'}
                                                />
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group controlId="to_date">
                                                <Form.Label>To Date <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="to_date"
                                                    value={formData.to_date}
                                                    onChange={handleChange}
                                                    min={formData.from_date}
                                                    required={activeTab === 'date-based'}
                                                    disabled={activeTab !== 'date-based'}
                                                />
                                            </Form.Group>
                                        </Col>

                                        {/* First Day Half Day Options */}
                                        <Col md={6}>
                                            <Form.Group controlId="first_day_half">
                                                <Form.Check
                                                    type="checkbox"
                                                    label="First Day Half Day"
                                                    name="first_day_half"
                                                    checked={formData.first_day_half}
                                                    onChange={handleChange}
                                                />
                                            </Form.Group>
                                        </Col>

                                        {formData.first_day_half && (
                                            <Col md={6}>
                                                <Form.Group controlId="first_day_period">
                                                    <Form.Label>First Day Period</Form.Label>
                                                    <Form.Select
                                                        name="first_day_period"
                                                        value={formData.first_day_period}
                                                        onChange={handleChange}
                                                    >
                                                        <option value="Morning">Morning</option>
                                                        <option value="Afternoon">Afternoon</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        )}

                                        {/* Last Day Half Day Options */}
                                        <Col md={6}>
                                            <Form.Group controlId="last_day_half">
                                                <Form.Check
                                                    type="checkbox"
                                                    label="Last Day Half Day"
                                                    name="last_day_half"
                                                    checked={formData.last_day_half}
                                                    onChange={handleChange}
                                                />
                                            </Form.Group>
                                        </Col>

                                        {formData.last_day_half && (
                                            <Col md={6}>
                                                <Form.Group controlId="last_day_period">
                                                    <Form.Label>Last Day Period</Form.Label>
                                                    <Form.Select
                                                        name="last_day_period"
                                                        value={formData.last_day_period}
                                                        onChange={handleChange}
                                                    >
                                                        <option value="Morning">Morning</option>
                                                        <option value="Afternoon">Afternoon</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        )}
                                    </Row>
                                </Tab>

                                <Tab eventKey="time-based" title="Time Based">
                                    <Row className="g-3 mt-2">
                                        <Col md={12}>
                                            <Form.Group controlId="time_slot_type">
                                                <Form.Label>Time Slot Type</Form.Label>
                                                <Form.Select
                                                    name="time_slot_type"
                                                    value={formData.time_slot_type}
                                                    onChange={handleChange}
                                                >
                                                    <option value="Morning">Morning</option>
                                                    <option value="Afternoon">Afternoon</option>
                                                    <option value="Morning and Afternoon">Morning and Afternoon</option>
                                                    <option value="Custom">Custom</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        {formData.time_slot_type === 'Custom' ? (
                                            <>
                                                <Col md={6}>
                                                    <Form.Group controlId="custom_start_time">
                                                        <Form.Label>Start Time <span className="text-danger">*</span></Form.Label>
                                                        <Form.Control
                                                            type="time"
                                                            name="custom_start_time"
                                                            value={formData.custom_start_time}
                                                            onChange={handleChange}
                                                            required={activeTab === 'time-based' && formData.time_slot_type === 'Custom'}
                                                            disabled={activeTab !== 'time-based' || formData.time_slot_type !== 'Custom'}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group controlId="custom_end_time">
                                                        <Form.Label>End Time <span className="text-danger">*</span></Form.Label>
                                                        <Form.Control
                                                            type="time"
                                                            name="custom_end_time"
                                                            value={formData.custom_end_time}
                                                            onChange={handleChange}
                                                            required={activeTab === 'time-based' && formData.time_slot_type === 'Custom'}
                                                            disabled={activeTab !== 'time-based' || formData.time_slot_type !== 'Custom'}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </>
                                        ) : (
                                            <>
                                                <Col md={6}>
                                                    <Form.Group controlId="from_datetime">
                                                        <Form.Label>From Date & Time <span className="text-danger">*</span></Form.Label>
                                                        <Form.Control
                                                            type="datetime-local"
                                                            name="from_datetime"
                                                            value={formData.from_datetime}
                                                            onChange={handleChange}
                                                            min={new Date().toISOString().slice(0, 16)}
                                                            required={activeTab === 'time-based' && formData.time_slot_type !== 'Custom'}
                                                            disabled={activeTab !== 'time-based' || formData.time_slot_type === 'Custom'}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group controlId="to_datetime">
                                                        <Form.Label>To Date & Time <span className="text-danger">*</span></Form.Label>
                                                        <Form.Control
                                                            type="datetime-local"
                                                            name="to_datetime"
                                                            value={formData.to_datetime}
                                                            onChange={handleChange}
                                                            min={formData.from_datetime}
                                                            required={activeTab === 'time-based' && formData.time_slot_type !== 'Custom'}
                                                            disabled={activeTab !== 'time-based' || formData.time_slot_type === 'Custom'}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </>
                                        )}
                                    </Row>
                                </Tab>
                            </Tabs>
                        </Col>

                        <Col md={12}>
                            <Form.Group controlId="purpose">
                                <Form.Label>Purpose <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="purpose"
                                    value={formData.purpose}
                                    onChange={handleChange}
                                    placeholder="Explain the purpose of on-duty..."
                                    required
                                />
                            </Form.Group>
                        </Col>

                        <Col md={12}>
                            <Form.Group controlId="destination">
                                <Form.Label>Destination</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="destination"
                                    value={formData.destination}
                                    onChange={handleChange}
                                    placeholder="Enter destination (optional)"
                                />
                            </Form.Group>
                        </Col>

                        {/* Summary Card */}
                        <Col md={12}>
                            <div className="card bg-light border-0">
                                <div className="card-header bg-transparent border-0 pb-0">
                                    <h6 className="mb-0 fw-bold">
                                        <i className="bi bi-card-checklist me-2"></i>
                                        Request Summary
                                    </h6>
                                </div>
                                <div className="card-body py-2">
                                    <Row>
                                        {activeTab === 'date-based' ? (
                                            <>
                                                <Col md={6}>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span className="text-muted">
                                                            <i className="bi bi-calendar-range me-2"></i>
                                                            Duration:
                                                        </span>
                                                        <span className="fw-bold">
                                                            {totalDays} days
                                                        </span>
                                                    </div>
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span className="text-muted">
                                                            <i className="bi bi-tag me-2"></i>
                                                            Request Type:
                                                        </span>
                                                        <span className="fw-bold">
                                                            {formData.request_type}
                                                        </span>
                                                    </div>
                                                </Col>
                                                <Col md={6}>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span className="text-muted">
                                                            <i className="bi bi-calendar3 me-2"></i>
                                                            Date Range:
                                                        </span>
                                                        <span className="fw-bold">
                                                            {formData.from_date && formData.to_date ? (
                                                                <>
                                                                    {new Date(formData.from_date).toLocaleDateString()} - {new Date(formData.to_date).toLocaleDateString()}
                                                                </>
                                                            ) : '--'}
                                                        </span>
                                                    </div>
                                                </Col>
                                            </>
                                        ) : (
                                            <>
                                                <Col md={6}>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span className="text-muted">
                                                            <i className="bi bi-clock me-2"></i>
                                                            Total Hours:
                                                        </span>
                                                        <span className="fw-bold">
                                                            {totalHours.toFixed(2)} hours
                                                        </span>
                                                    </div>
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span className="text-muted">
                                                            <i className="bi bi-tag me-2"></i>
                                                            Request Type:
                                                        </span>
                                                        <span className="fw-bold">
                                                            {formData.request_type}
                                                        </span>
                                                    </div>
                                                </Col>
                                                <Col md={6}>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span className="text-muted">
                                                            <i className="bi bi-clock-history me-2"></i>
                                                            Time Slot:
                                                        </span>
                                                        <span className="fw-bold">
                                                            {formData.time_slot_type}
                                                        </span>
                                                    </div>
                                                </Col>
                                            </>
                                        )}
                                    </Row>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={loading || !formData.emp_id || !formData.purpose.trim()}
                    >
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Submitting...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-send me-2"></i>
                                Submit Request
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default AddOndutyRequest;
